/**
 * TEMP-H-004: Update Stage Settings Hook (PATCH)
 * 
 * Handles stage settings updates with optimistic UX, validation, and rollback.
 * Integrates with Events v2 for audit trail and provides toast notifications.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  StageSettingsConfig,
  UpdateStageSettingsRequest,
  UpdateStageSettingsResponse,
  StageSettingsError,
  ValidationResult,
  ThresholdValidationRules,
  StageSettingsAuditEvent
} from '@/types/stage-settings';
import {
  DEFAULT_VALIDATION_RULES,
  stageSettingsKeys
} from '@/types/stage-settings';
import type { StageName } from '@/lib/shared-types';
import { joinApiPath } from '@/lib/api-config';
import { getApiHeaders } from '@/lib/api-base';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { useDashboardServerStore } from '@/stores/dashboard-server';

// Legacy type alias for backward compatibility
type StageType = StageName;

/**
 * Validate stage settings before submission
 */
function validateSettings(
  updates: UpdateStageSettingsRequest,
  current: StageSettingsConfig,
  rules: ThresholdValidationRules = DEFAULT_VALIDATION_RULES
): ValidationResult {
  const errors: Record<string, string> = {};
  const warnings: Record<string, string> = {};

  // Validate passMin
  if (updates.passMin !== undefined) {
    if (updates.passMin < rules.passMin.min) {
      errors.passMin = `Pass minimum must be at least ${(rules.passMin.min * 100).toFixed(0)}%`;
    }
    if (updates.passMin > rules.passMin.max) {
      errors.passMin = `Pass minimum cannot exceed ${(rules.passMin.max * 100).toFixed(0)}%`;
    }
  }

  // Validate fallbackMin
  if (updates.fallbackMin !== undefined) {
    if (updates.fallbackMin < rules.fallbackMin.min) {
      errors.fallbackMin = `Fallback minimum must be at least ${(rules.fallbackMin.min * 100).toFixed(0)}%`;
    }
    if (updates.fallbackMin > rules.fallbackMin.max) {
      errors.fallbackMin = `Fallback minimum cannot exceed ${(rules.fallbackMin.max * 100).toFixed(0)}%`;
    }
  }

  // Cross-field validation: passMin > fallbackMin
  if (rules.passMinMustBeGreaterThanFallback) {
    const newPassMin = updates.passMin ?? current.activePersona.passMin;
    const newFallbackMin = updates.fallbackMin ?? current.activePersona.fallbackMin;
    
    if (newPassMin <= newFallbackMin) {
      errors.passMin = 'Pass minimum must be greater than fallback minimum';
    }
  }

  // Validate category thresholds
  if (updates.categoryThresholds) {
    Object.entries(updates.categoryThresholds).forEach(([category, threshold]) => {
      if (threshold < rules.categoryThresholds.min) {
        errors[`categoryThresholds.${category}`] = `${category} threshold must be at least ${(rules.categoryThresholds.min * 100).toFixed(0)}%`;
      }
      if (threshold > rules.categoryThresholds.max) {
        errors[`categoryThresholds.${category}`] = `${category} threshold cannot exceed ${(rules.categoryThresholds.max * 100).toFixed(0)}%`;
      }
    });

    // Check category count limit
    if (rules.maxCategoryCount && Object.keys(updates.categoryThresholds).length > rules.maxCategoryCount) {
      errors.categoryThresholds = `Cannot have more than ${rules.maxCategoryCount} categories`;
    }
  }

  // Validate category weights
  if (updates.categoryWeights) {
    let totalWeight = 0;
    
    Object.entries(updates.categoryWeights).forEach(([category, weight]) => {
      if (weight < rules.categoryWeights.min) {
        errors[`categoryWeights.${category}`] = `${category} weight must be at least ${rules.categoryWeights.min}`;
      }
      if (weight > rules.categoryWeights.max) {
        errors[`categoryWeights.${category}`] = `${category} weight cannot exceed ${rules.categoryWeights.max}`;
      }
      totalWeight += weight;
    });

    // Check sum constraint
    if (rules.categoryWeights.sumMustEqual && Math.abs(totalWeight - rules.categoryWeights.sumMustEqual) > 0.001) {
      errors.categoryWeights = `Category weights must sum to ${rules.categoryWeights.sumMustEqual} (currently ${totalWeight.toFixed(3)})`;
    }
  }

  // Validate retry limit
  if (updates.retryLimit !== undefined) {
    if (updates.retryLimit < rules.retryLimit.min) {
      errors.retryLimit = `Retry limit must be at least ${rules.retryLimit.min}`;
    }
    if (updates.retryLimit > rules.retryLimit.max) {
      errors.retryLimit = `Retry limit cannot exceed ${rules.retryLimit.max}`;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings: Object.keys(warnings).length > 0 ? warnings : undefined
  };
}

/**
 * Create optimistic update for immediate UI feedback
 */
function createOptimisticUpdate(
  current: StageSettingsConfig,
  updates: UpdateStageSettingsRequest
): StageSettingsConfig {
  const updatedActivePersona = {
    ...current.activePersona,
    ...(updates.passMin !== undefined && { passMin: updates.passMin }),
    ...(updates.fallbackMin !== undefined && { fallbackMin: updates.fallbackMin }),
    ...(updates.categoryThresholds && { 
      categoryThresholds: { ...current.activePersona.categoryThresholds, ...updates.categoryThresholds }
    }),
    ...(updates.categoryWeights && {
      categoryWeights: { ...current.activePersona.categoryWeights, ...updates.categoryWeights }
    }),
    ...(updates.retryLimit !== undefined && { retryLimit: updates.retryLimit }),
  };

  return {
    ...current,
    activePersona: updatedActivePersona,
    overriddenFromLaunch: true,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Create audit event for Events v2 integration
 */
function createAuditEvent(
  executionId: string,
  stage: StageType,
  userId: string,
  before: StageSettingsConfig,
  after: StageSettingsConfig,
  reason?: string
): StageSettingsAuditEvent {
  const changes: StageSettingsAuditEvent['changes'] = [];

  // Compare values and record changes
  if (before.activePersona.passMin !== after.activePersona.passMin) {
    changes.push({
      field: 'passMin',
      before: before.activePersona.passMin,
      after: after.activePersona.passMin
    });
  }

  if (before.activePersona.fallbackMin !== after.activePersona.fallbackMin) {
    changes.push({
      field: 'fallbackMin',
      before: before.activePersona.fallbackMin,
      after: after.activePersona.fallbackMin
    });
  }

  if (before.activePersona.retryLimit !== after.activePersona.retryLimit) {
    changes.push({
      field: 'retryLimit',
      before: before.activePersona.retryLimit,
      after: after.activePersona.retryLimit
    });
  }

  // Check category threshold changes
  const beforeCategories = before.activePersona.categoryThresholds;
  const afterCategories = after.activePersona.categoryThresholds;
  
  const allCategories = new Set([
    ...Object.keys(beforeCategories),
    ...Object.keys(afterCategories)
  ]);

  allCategories.forEach(category => {
    if (beforeCategories[category] !== afterCategories[category]) {
      changes.push({
        field: `categoryThresholds.${category}`,
        before: beforeCategories[category],
        after: afterCategories[category]
      });
    }
  });

  return {
    eventType: 'stage_settings_updated',
    executionId,
    stage,
    userId,
    timestamp: new Date().toISOString(),
    changes,
    reason,
    ...(before.activePersona.personaSlug !== after.activePersona.personaSlug && {
      personaChanged: {
        from: before.activePersona.personaSlug,
        to: after.activePersona.personaSlug
      }
    })
  };
}

/**
 * Hook for updating stage settings with optimistic UX
 */
export function useUpdateStageSettings(
  executionId: string,
  stage: StageType,
  options: {
    onSuccess?: (response: UpdateStageSettingsResponse) => void;
    onError?: (error: StageSettingsError) => void;
    validationRules?: ThresholdValidationRules;
  } = {}
) {
  const { user } = useServerAuth();
  const queryClient = useQueryClient();
  const addNotification = useDashboardServerStore(state => state.addNotification);

  const mutation = useMutation({
    mutationFn: async (updates: UpdateStageSettingsRequest): Promise<UpdateStageSettingsResponse> => {
      // Get current settings for validation
      const currentSettings = queryClient.getQueryData<StageSettingsConfig>(
        stageSettingsKeys.stage(executionId, stage)
      );

      if (!currentSettings) {
        const error: StageSettingsError = {
          type: 'network',
          message: 'Current settings not available for validation'
        };
        throw error;
      }

      // Validate before sending
      const validation = validateSettings(updates, currentSettings, options.validationRules);
      if (!validation.isValid) {
        const error: StageSettingsError = {
          type: 'validation',
          message: 'Validation failed',
          details: validation.errors
        };
        throw error;
      }

      // Send PATCH request
      const response = await fetch(
        joinApiPath('executions', executionId, 'stages', stage, 'settings'),
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            ...getApiHeaders(),
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`
        }));

        let errorType: StageSettingsError['type'] = 'network';
        let errorMessage = error.message || 'Failed to update stage settings';

        if (response.status === 400) {
          errorType = 'validation';
          errorMessage = error.message || 'Invalid settings data';
        } else if (response.status === 401) {
          errorType = 'permission';
          errorMessage = 'Authentication required';
        } else if (response.status === 403) {
          errorType = 'permission';
          errorMessage = 'You do not have permission to update settings';
        } else if (response.status === 409) {
          errorType = 'conflict';
          errorMessage = 'Settings were modified by another user';
        }

        const settingsError: StageSettingsError = {
          type: errorType,
          message: errorMessage,
          details: error.details
        };
        throw settingsError;
      }

      return response.json();
    },

    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: stageSettingsKeys.stage(executionId, stage)
      });

      // Get current data
      const previousSettings = queryClient.getQueryData<StageSettingsConfig>(
        stageSettingsKeys.stage(executionId, stage)
      );

      if (previousSettings) {
        // Apply optimistic update
        const optimisticSettings = createOptimisticUpdate(previousSettings, updates);
        
        queryClient.setQueryData(
          stageSettingsKeys.stage(executionId, stage),
          optimisticSettings
        );
      }

      return { previousSettings };
    },

    onError: (error: StageSettingsError, _variables, context) => {
      // Rollback optimistic update
      if (context?.previousSettings) {
        queryClient.setQueryData(
          stageSettingsKeys.stage(executionId, stage),
          context.previousSettings
        );
      }

      // Show error notification
      addNotification({
        type: 'error',
        title: 'Settings Update Failed',
        message: error.message,
        duration: 5000
      });

      // Call custom error handler
      options.onError?.(error);
    },

    onSuccess: (response, variables, context) => {
      // Update cache with server response
      if (response.settings) {
        queryClient.setQueryData(
          stageSettingsKeys.stage(executionId, stage),
          response.settings
        );
      }

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: stageSettingsKeys.execution(executionId)
      });

      // Show success notification
      addNotification({
        type: 'success',
        title: 'Settings Updated',
        message: response.message || 'Stage settings updated successfully',
        duration: 3000
      });

      // Create audit event if user info available
      if (user && context?.previousSettings && response.settings) {
        const auditEvent = createAuditEvent(
          executionId,
          stage,
          user.id,
          context.previousSettings,
          response.settings,
          variables.reason
        );

        // Emit audit event (would integrate with Events v2 WebSocket)
        console.log('🔍 Stage settings audit event:', auditEvent);
      }

      // Call custom success handler
      options.onSuccess?.(response);
    }
  });

  // Validation helper
  const validateBeforeSubmit = useCallback((updates: UpdateStageSettingsRequest) => {
    const currentSettings = queryClient.getQueryData<StageSettingsConfig>(
      stageSettingsKeys.stage(executionId, stage)
    );

    if (!currentSettings) {
      return {
        isValid: false,
        errors: { general: 'Current settings not loaded' }
      };
    }

    return validateSettings(updates, currentSettings, options.validationRules);
  }, [executionId, stage, queryClient, options.validationRules]);

  return {
    // Mutation functions
    updateSettings: mutation.mutateAsync,
    updateSettingsSync: mutation.mutate,
    
    // Validation
    validate: validateBeforeSubmit,
    
    // State
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error as StageSettingsError | null,
    isSuccess: mutation.isSuccess,
    
    // Reset
    reset: mutation.reset,
    
    // Last response
    lastResponse: mutation.data,
  };
}